/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  AuthenticationErrorMessage,
  ERROR_CODE,
  FaroFeatures,
  getRecaptchaToken,
  IBrowserError,
  IErrorMessage,
  prodModeLog,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  retryOnRecaptchaInvalid,
  RecaptchaTokenAction,
  useCaptcha,
  useLogger,
} from "@advapacs/common";
import { ErrorMessage } from "@advapacs/common";
import { useNavigate, useLocation } from "react-router-dom";
import { ILogin, ISaml } from "@src/types/login";
import {
  getOrganisationSettings,
  getWebAuthnChallenge,
  submitLogin,
  submitLoginRetry,
  submitTwoFaLogin,
  webAuthnAuthenticate,
} from "./ducks/operations";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import OtpInput from "react18-input-otp";
import { LoginAction, LoginFormType } from "./ducks/slices";
import { useAppDispatch } from "@src/config/store";
import {
  getSubDomain,
  getTypeDomain,
  getUrlOrganisation,
} from "@src/utils/utils";
import { useUnleashContext } from "@unleash/proxy-client-react";
import { OriginalLoginForm } from "./OriginalLoginForm";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  delayTime as turnstileDelayTime,
  reCaptchaDelay,
} from "@advapacs/common/src/utils/utils";
import {
  GOOGLE_RECAPTCHA_ENTERPRISE,
  TURNSTILE_SITE_KEY,
} from "@src/config/constants";
import { getBrandStyleButton, useBrand } from "@src/pages/login/BrandContext";
import BrandLaddaButton from "@src/components/BrandLaddaButton";
import { LogLevel } from "@grafana/faro-web-sdk";

interface LoginFormProps {
  isSaml: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({ isSaml }) => {
  // ======================= STATES ==================================
  const { t } = useTranslation(["login", "2fa"]);
  const updateUnleashContext = useUnleashContext();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const captchaType = useCaptcha();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const executeRecaptchaRef = useRef(executeRecaptcha);
  const triggerOneTimeCallRetry = useRef(false);
  const lastLoginRequest = useRef<ILogin | null>(null); // last AUTHENTICATE_USER body sent with grecaptcha, re-sent by the 1050 retry.
  const shouldShowRecaptchaRiskToken = useRef(false); // dont' show error message with recaptcha token in first-time.
  const currentRef = useRef(false);
  const oneTimeCallDispatchingLogin = useRef(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  );
  const tempTurnstileAction = useRef({
    action: "",
    data: {},
    challenge: "",
  });
  const {
    twoFaErrorMessage,
    loading,
    pathname,
    loginFormType,
    isAuthenticated,
    errorMessage,
    samlErrorMessage,
    organisationSettings,
    organisationSettingsErrorMessage,
    showVerifyOrganisation,
    forgottenOrganisationErrorMessage,
    hasForgottenOrganisation,
  } = useSelector((state: IRootState) => state.loginReducer);
  const [isOrganisationLoginOriginal, setIsOrganisationLoginOriginal] =
    useState(false);
  const [turnstileError, setTurnstileError] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [organisationId, setOrganisationId] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [smalLoading, setSmalLoading] = useState(false);
  const [selectedSaml, setSelectedSaml] = useState<ISaml>();
  const [socialErrorMessage, setSocialErrorMessage] = useState(null);
  const [fromSocialAccount, setFromSocialAccount] = useState(false);
  const [socialEmail, setSocialEmail] = useState("");
  const [socialToken, setSocialToken] = useState("");
  const [samlError, setSamlError] = useState<ErrorMessage | null | undefined>(
    samlErrorMessage
  );
  const [browserError, setBrowserError] = useState<IBrowserError | null>(null);
  const [stateForm, setStateForm] = useState<ILogin>({
    username: "",
    password: "",
    token: "",
  });

  const domainType = useMemo(() => getTypeDomain(), []);
  const { brand } = useBrand();
  const brandStyleButton = getBrandStyleButton(brand);
  const btnClass = `btn ${
    Object.keys(brandStyleButton).length ? "" : "btn-primary"
  } btn-block btn-ladda btn-ladda-spinner`;

  // ======================= LOGGER ===========================
  const logger = useLogger("[LoginForm]");

  // ======================== FUNCTIONS =======================

  const validateTurnstile = () => {
    if (!turnstileRef?.current || !window.turnstile) {
      setCaptchaLoading(false);
      return false;
    }
    return true;
  };

  const handleBrowserError = (err: IErrorMessage) => {
    const errMsg =
      typeof err === "string" ? err : err?.message || "Unknown error messsage";
    setBrowserError({ message: errMsg });
  };

  const generateRecaptchaToken = async (
    action: RecaptchaTokenAction,
    retries = RECAPTCHA_MAX_RETRIES
  ): Promise<string> => {
    setBrowserError(null);
    setCaptchaLoading(true);
    const currentExecuteRecaptcha = executeRecaptchaRef.current; // alway fresh
    // Script not loaded yet — retry until ready, then give up
    if (!currentExecuteRecaptcha) {
      if (retries > 0) {
        console.warn("Re-trigger waiting gRecaptcha loaded.");
        await reCaptchaDelay(RECAPTCHA_SCRIPT_LOAD_RETRIES); // WAIT 8S
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      logger({
        message: "reCAPTCHA script not loaded yet.",
        action: "generateRecaptchaToken",
        level: LogLevel.INFO,
        feature: FaroFeatures.RECAPTCHA,
      });
      return Promise.reject(new Error("reCAPTCHA script not loaded yet."));
    }

    // Script ready — attempt token generation with backoff retry
    try {
      return await getRecaptchaToken(
        action,
        GOOGLE_RECAPTCHA_ENTERPRISE,
        currentExecuteRecaptcha
      );
    } catch (error) {
      if (retries > 0) {
        const delayTime =
          RECAPTCHA_RETRY_DELAY_MS * (RECAPTCHA_MAX_RETRIES - retries + 1);
        console.warn(
          `reCAPTCHA failed. Retrying in ${
            delayTime / 1000
          }s... (${retries} retries left)`
        );
        await reCaptchaDelay(delayTime);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      logger({
        level: LogLevel.ERROR,
        error: `reCAPTCHA token failed after retries: ${
          (error as IErrorMessage)?.message || error
        }`,
        action: "generateRecaptchaToken",
        feature: FaroFeatures.RECAPTCHA,
        args: { action },
      });
      return Promise.reject((error as IErrorMessage)?.message || error);
    }
  };

  const processWebAuthnAuthentication = useCallback(async () => {
    if (errorMessage && errorMessage.challenge) {
      triggerOneTimeCallRetry.current = true; // WebAuthn uses its own challenge, the 1050 retry can't re-send it.
      const dataRequest = fromSocialAccount // social login case.
        ? {
            username: socialEmail,
            token: socialToken, // token from params.
          }
        : {
            ...stateForm,
          };
      // logs

      if (fromSocialAccount) {
        // log  data request.
        prodModeLog(JSON.stringify(dataRequest));
      }
      if (captchaType?.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        setTurnstileError(""); // reset error.
        // turnstile flow.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        tempTurnstileAction.current.action = "webAuthnAuthenticate"; //
        tempTurnstileAction.current.data = dataRequest;
        tempTurnstileAction.current.challenge = errorMessage.challenge;
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.CP_LOGIN,
          async: false, // prevent async retries
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (processWebAuthnAuthentication function)"
        );
      } else {
        try {
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.CP_LOGIN
          );
          if (!recaptchaToken) throw Error("Missing Recaptcha Token"); // assert
          dispatch(
            webAuthnAuthenticate(
              dataRequest,
              errorMessage.challenge,
              pathname,
              recaptchaToken
            )
          );
        } catch (err) {
          handleBrowserError(err as IErrorMessage);
        } finally {
          setCaptchaLoading(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    errorMessage,
    stateForm,
    pathname,
    fromSocialAccount,
    socialEmail,
    socialToken,
  ]);

  const onNextOrganisation = async () => {
    window.location.href = getUrlOrganisation(organisationId);
  };

  const onRedirectToSamlIdp = (saml: ISaml) => {
    setSelectedSaml(saml);
    setSmalLoading(true);
    setSamlError(undefined);
    axios
      .get(`/auth/saml2/${saml.idpId}`)
      .then((res) => {
        window.location.href = res.data.redirectTo;
      })
      .catch((error) => {
        setSmalLoading(false);
        setSamlError(error?.response?.data);
      });
  };

  const handleRetryError = (err: IErrorMessage) => {
    dispatch(LoginAction.setErrorMessageWithoutLoading(err)); // show normal error
    dispatch(LoginAction.stopLoading());
    setCaptchaLoading(false);
    setStateForm({ ...stateForm, password: "" });
    // reset the social data
    setSocialEmail("");
    setSocialToken("");
    shouldShowRecaptchaRiskToken.current = true; // show error message when retry failed.
  };

  // handle API throw BROWSER_ERROR with invalid token: re-submit the last login request (password, social or MFA) with a fresh token.
  const handleRetrySubmitFunc = async () => {
    const loginRequest = lastLoginRequest.current;
    if (!loginRequest) return;
    const logArgs = {
      isMfa: !!loginRequest.totp,
      isSocial: !!loginRequest.token,
    }; // never log credentials
    logger({
      level: LogLevel.WARN,
      message: "Login rejected with reCAPTCHA invalid (1050), retrying.",
      action: "handleRetrySubmitFunc",
      feature: FaroFeatures.RECAPTCHA,
      args: logArgs,
    });
    try {
      await retryOnRecaptchaInvalid(async () => {
        const recaptchaToken = await generateRecaptchaToken(
          RecaptchaTokenAction.CP_LOGIN
        );
        if (!recaptchaToken) throw Error("Missing Recaptcha Token"); // assert
        // AUTHENTICATE_USER takes password, social (token) and MFA (totp) logins.
        return submitLoginRetry(dispatch, loginRequest, recaptchaToken);
      });
      logger({
        level: LogLevel.INFO,
        message: "Login succeeded after reCAPTCHA retry.",
        action: "handleRetrySubmitFunc",
        feature: FaroFeatures.RECAPTCHA,
        args: logArgs,
      });
    } catch (err) {
      const code = (err as IErrorMessage)?.code;
      logger({
        level:
          code === ERROR_CODE.RECAPTCHA_INVALID
            ? LogLevel.ERROR
            : LogLevel.WARN,
        error: `Login reCAPTCHA retry stopped (code ${code ?? "none"}).`,
        message: `Login reCAPTCHA retry stopped (code ${code ?? "none"}).`,
        action: "handleRetrySubmitFunc",
        feature: FaroFeatures.RECAPTCHA,
        args: { ...logArgs, code },
      });
      if (code === ERROR_CODE.REQUIRE_MFA) {
        // credentials accepted, MFA required: show the MFA step like a normal submit.
        dispatch(
          LoginAction.loginFail({
            err: err as AuthenticationErrorMessage,
            pathname,
          })
        );
        setCaptchaLoading(false);
        return;
      }
      // after retry will stop loading action and throw error
      handleRetryError(err as IErrorMessage);
    }
  };

  const onLogin = async (e: FormEvent) => {
    try {
      e.preventDefault();
      localStorage.removeItem("region");

      shouldShowRecaptchaRiskToken.current = false;
      triggerOneTimeCallRetry.current = false; // reset onetime call state to re-init error.

      if (captchaType?.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        setTurnstileError(""); // reset error.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        // turnstile flow.
        tempTurnstileAction.current.action = "submitLogin"; //
        tempTurnstileAction.current.data = stateForm;
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.CP_LOGIN,
          async: false, // prevent async retries
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (submit button)"
        );
      } else {
        try {
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.CP_LOGIN
          );

          if (!recaptchaToken) throw Error("Missing Recaptcha Token"); // assert
          lastLoginRequest.current = stateForm;
          dispatch(submitLogin(stateForm as ILogin, pathname, recaptchaToken));
          setCaptchaLoading(false);
        } catch (err) {
          handleBrowserError(err as IErrorMessage);
        }
      }
    } catch (error) {
      // throw error for next ticket.
      console.error({ error });
      if ((error as IErrorMessage)?.code === 3329) {
        setTurnstileError((error as IErrorMessage)?.message);
      }
    }
  };

  const loginOtp = async () => {
    if (verifyCode.length === 6) {
      triggerOneTimeCallRetry.current = false; // new submit: allow the 1050 retry again.
      const dataRequest = fromSocialAccount // social login case.
        ? {
            username: socialEmail,
            totp: parseInt(verifyCode, 10),
            token: socialToken, // token from params.
          }
        : {
            ...stateForm,
            totp: parseInt(verifyCode, 10),
          };
      if (captchaType?.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        setTurnstileError(""); // reset error.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        // turnstile flow.
        tempTurnstileAction.current.action = "submitTwoFaLogin"; //
        tempTurnstileAction.current.data = dataRequest;
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.CP_LOGIN,
          async: false, // prevent async retries
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (submitTwoFaLogin button)"
        );
      } else {
        try {
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.CP_LOGIN
          );
          if (!recaptchaToken) throw Error("Missing Recaptcha Token"); // assert

          lastLoginRequest.current = dataRequest;
          dispatch(submitTwoFaLogin(dataRequest, pathname, recaptchaToken));
        } catch (err) {
          handleBrowserError(err as IErrorMessage);
        } finally {
          setCaptchaLoading(false);
        }
      }
    }
  };

  const handleTrustDeviceChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setTrustDevice(checked);
    setStateForm({ ...stateForm, trustDevice: checked });
  };

  const reload = () => {
    dispatch(LoginAction.clearAuthentication({ isReloadBrowser: false }));
    window.location.href = "/";
  };

  const onChange2FaType = useCallback(
    (type?: LoginFormType) => {
      setCaptchaLoading(false);
      dispatch(LoginAction.setTwoFaTypeAuthenticate(type));
    },
    [dispatch]
  );

  const optForm = () => (
    <form className="mt-3">
      <div className="form-group text-center">
        <h5 className="mb-0">{t("2fa:multi_factor_authentication")}</h5>
        <span className="d-block text-muted mb-2">
          {t("2fa:enter_pincode")}
        </span>
      </div>
      <div
        tabIndex={0}
        role="textbox"
        className="form-group"
        onKeyPress={(e) => e.key === "Enter" && loginOtp()}
      >
        <OtpInput
          shouldAutoFocus
          isInputNum
          className="form-control verify-code"
          value={verifyCode}
          onChange={setVerifyCode}
          numInputs={6}
        />

        {twoFaErrorMessage && (
          <div className="alert bg-warning text-white alert-styled-left error-container animated fadeInDown m-t-sm">
            {twoFaErrorMessage.message}
          </div>
        )}
      </div>
      {(!organisationId || organisationSettings?.allowTrustedDevice) && (
        <div className="form-group">
          <div className="checkbox-item">
            <label className="form-check-label form" htmlFor="dc_ls_c">
              <input
                type="checkbox"
                className="form-check-input"
                onChange={handleTrustDeviceChange}
                checked={trustDevice}
                id="dc_ls_c"
              />
              {t("trust_this_device", {
                days: organisationSettings?.trustDuration || 30,
              })}
            </label>
          </div>
        </div>
      )}

      <div className="form-group">
        <button
          type="button"
          disabled={
            !verifyCode || verifyCode.length < 6 || captchaLoading || loading
          }
          onClick={loginOtp}
          style={brandStyleButton}
          className={
            Object.keys(brandStyleButton).length
              ? "btn btn-block ladda-btn px-3"
              : "btn btn-primary ladda-btn px-3 btn-block"
          }
        >
          {loading ||
            (captchaLoading && <i className="icon-spinner3 mr-2 spinner" />)}
          {t("2fa:verify_code")}
          <div className="icon-circle-right2 ml-2" />
        </button>
      </div>
      <div className="form-group">
        <button
          onClick={reload}
          type="button"
          className="btn btn-light m-t-2 btn-block btn-ladda"
        >
          <i className="fa fa-angle-double-left ml-2" />
          {t("back_to_login")}
        </button>
      </div>
    </form>
  );

  const selection2FAForm = () => {
    return (
      <>
        <div className="text-center mb-1 mt-3">
          <h5 className="mb-0">{t("2fa:multi_factor_authentication")}</h5>
          <span className="d-block text-muted pb-2 mb-1">
            {t("2fa:note_webauthn_device")}
          </span>
        </div>
        {(!organisationId || organisationSettings?.allowTrustedDevice) && (
          <div className="form-group">
            <div className="checkbox-item">
              <label className="form-check-label form" htmlFor="dc_ls_c">
                <input
                  type="checkbox"
                  className="form-check-input"
                  onChange={handleTrustDeviceChange}
                  checked={trustDevice}
                  id="dc_ls_c"
                />
                {t("trust_this_device", {
                  days: organisationSettings?.trustDuration || 30,
                })}
              </label>
            </div>
          </div>
        )}
        {twoFaErrorMessage?.message && (
          <div className="alert bg-warning text-white alert-styled-left error-container">
            {twoFaErrorMessage.message}
          </div>
        )}
        <div className="form-group">
          <BrandLaddaButton
            className={btnClass}
            type="submit"
            loading={loading || captchaLoading}
            disabled={loading || captchaLoading}
            onClickHandler={processWebAuthnAuthentication}
            btnName={t("2fa:sign_in_via_WebAuthn")}
            // @ts-ignore
            style={brandStyleButton}
          ></BrandLaddaButton>
          <button
            type="button"
            className={
              Object.keys(brandStyleButton).length
                ? "btn btn-block ladda-btn px-3"
                : "btn btn-primary btn-block text-center"
            }
            onClick={() => onChange2FaType(LoginFormType.OTP)}
            style={brandStyleButton}
          >
            {t("2fa:sign_in_mfa_code")}
          </button>

          <button
            onClick={reload}
            type="button"
            className="btn btn-light m-t-2 btn-block btn-ladda"
          >
            <i className="fa fa-angle-double-left ml-2" />
            {t("login:back_to_login")}
          </button>
        </div>
      </>
    );
  };

  const twoFactorContainerForm = (): JSX.Element | null => {
    if (loginFormType === LoginFormType.OTP) {
      return optForm();
    }
    if (loginFormType === LoginFormType.WEBAUTHN) {
      return (
        <div className="text-center mb-3 mt-3">
          <h5 className="mb-0">{t("2fa:multi_factor_authentication")}</h5>
          <span className="d-block text-muted pb-2 mb-4">
            {t("2fa:note_webauthn_device")}
          </span>
          <button
            onClick={reload}
            type="button"
            className="btn btn-light m-t-2 btn-block btn-ladda"
          >
            <i className="fa fa-angle-double-left ml-2" />
            {t("login:back_to_login")}
          </button>
        </div>
      );
    }
    if (loginFormType === LoginFormType.TWOFA) {
      return selection2FAForm();
    }
    return null;
  };

  const handleLoginOrganisationFlow = async () => {
    try {
      await dispatch(getOrganisationSettings(getSubDomain()));
      setIsOrganisationLoginOriginal(true);
      // show only login form for org when it have settings.
    } catch (error) {
      setIsOrganisationLoginOriginal(false);
    }
  };

  const submitSocialLogin = async (token: string, email: string) => {
    try {
      const userData = {
        username: email,
        token,
      };
      if (captchaType?.provider === "turnstile") {
        await turnstileDelayTime(1000); // waiting turnstile rendered frame.
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        setTurnstileError(""); // reset error.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        // turnstile flow.
        tempTurnstileAction.current.action = "submitLogin"; //
        tempTurnstileAction.current.data = userData;
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.CP_LOGIN,
          async: false, // prevent async retries
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (submit button with social case.)"
        );
      } else {
        try {
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.CP_LOGIN
          );

          if (!recaptchaToken) throw Error("Missing Recaptcha Token"); // assert
          lastLoginRequest.current = userData;
          dispatch(submitLogin(userData, pathname, recaptchaToken));
        } catch (err) {
          handleBrowserError(err as IErrorMessage);
        } finally {
          setCaptchaLoading(false);
        }
      }
    } catch (error) {
      // throw error for next ticket.
      console.error({ error });
    }
  };

  const shouldAutoLoginWithSocialAccount = useMemo(() => {
    return queryParams.get("token") !== "" && queryParams.get("email");
  }, [queryParams]);

  // turnstile callback after execute generate token
  const handleCallbackFunction = (turnstileToken: string) => {
    if (!turnstileToken) return;
    switch (tempTurnstileAction.current.action) {
      case "submitLogin":
        dispatch(
          submitLogin(
            tempTurnstileAction.current.data as ILogin, // form data
            pathname, // redirect path
            turnstileToken, // captcha token from google or turnstile
            true // update header params
          )
        );

        break;
      case "webAuthnAuthenticate":
        dispatch(
          webAuthnAuthenticate(
            tempTurnstileAction.current.data as ILogin,
            tempTurnstileAction.current.challenge,
            pathname,
            turnstileToken,
            true
          )
        );
        break;
      case "submitTwoFaLogin":
        dispatch(
          submitTwoFaLogin(
            tempTurnstileAction.current.data as ILogin,
            pathname,
            turnstileToken,
            true
          )
        );
        break;
      default:
        break;
    }
    prodModeLog("Finish function " + tempTurnstileAction.current.action);
    // reset after callback called.
    window.turnstile?.reset(turnstileRef.current!); // reset
    tempTurnstileAction.current.action = "";
    tempTurnstileAction.current.challenge = "";
    tempTurnstileAction.current.data = {};

    setCaptchaLoading(false);
  };

  /**
   * Effects.
   */

  // handle login with social account form oauth callback
  useEffect(() => {
    const isIndividualLoginForm = getTypeDomain() === "individual";
    if (
      shouldAutoLoginWithSocialAccount &&
      !isAuthenticated &&
      isIndividualLoginForm &&
      !oneTimeCallDispatchingLogin.current
    ) {
      // set state
      setSocialEmail(queryParams.get("email") as string);
      setSocialToken(queryParams.get("token") as string);

      setFromSocialAccount(true);
      // sometime recaptcha v3 script still loading, that's why we should make sure executeRecaptcha existed.
      dispatch(LoginAction.loginStart()); // make ladda button working.
      oneTimeCallDispatchingLogin.current = true; // reset state.
      submitSocialLogin(
        queryParams.get("token") as string,
        queryParams.get("email") as string
      );

      // replace link without effect for secure token and email
      window.history.replaceState(null, "", "/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutoLoginWithSocialAccount,
    isAuthenticated,
    executeRecaptcha, // re-callback.
  ]);

  // global settings for unleash
  useEffect(() => {
    updateUnleashContext({
      region: "global",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationSettings]);

  useEffect(() => {
    if (!isSaml) return;
    const failureCode = queryParams.get("failureCode");
    if (failureCode === "1013") {
      navigate("/login");
      const failureMessage = queryParams.get("failureMessage");
      dispatch(
        LoginAction.setSAMLErrorMessage({
          code: failureCode,
          message: failureMessage || "",
          type: "SAMLException",
          status: "401",
        })
      );
      return;
    }
    if (failureCode && failureCode === "1010") {
      const username = queryParams.get("username");
      const samlSecret = queryParams.get("secret");
      const token = queryParams.get("token"); // social login case.
      const email = queryParams.get("email"); // social login case.
      if (username && samlSecret && samlSecret !== stateForm.samlSecret) {
        setStateForm({
          ...stateForm,
          username: (username || email) as string, // SAML for social case should user email or username based url callback response.
          token: token as string,
          samlSecret: samlSecret as string,
        });
        dispatch(
          getWebAuthnChallenge(
            (username || email) as string, // SAML for social case should user email or username based url callback response.
            samlSecret,
            queryParams.get("failureMessage") || ""
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isSaml, queryParams]);

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  // handle reset form based on error message.
  useEffect(() => {
    if (
      twoFaErrorMessage ||
      errorMessage?.code === "1004" || // Invalid credentials.
      errorMessage?.code === "1005" || // invalid MFA
      errorMessage?.code === "429" // rate limited, also after the 1050 retries
    ) {
      shouldShowRecaptchaRiskToken.current = true;
      setCaptchaLoading(false);
      setStateForm({ ...stateForm, password: "" });
      return;
    }

    // twoFaErrorMessage is handled above, so only errorMessage can carry the 1050 here.
    if (
      errorMessage?.code === ERROR_CODE.RECAPTCHA_INVALID && // ["RecaptchaInvalidException"].includes(errorMessage?.type)
      !triggerOneTimeCallRetry?.current
    ) {
      triggerOneTimeCallRetry.current = true; // prevent call multiple time.
      if (captchaType.provider === "grecaptcha" && lastLoginRequest.current) {
        // BROWSER ERROR Invalid reCaptcha token.
        // handle retry function.
        handleRetrySubmitFunc();
      } else {
        // for Turnstile is quite take a long time to generate token, we don't need the retry function for it.
        shouldShowRecaptchaRiskToken.current = true; // show error.
        setStateForm({ ...stateForm, password: "" });
        setCaptchaLoading(false);
      }
      return;
    }
    if (errorMessage?.code === ERROR_CODE.RECAPTCHA_INVALID) {
      // 1050 that is not retried (WebAuthn) or still failing after the retries: show it.
      shouldShowRecaptchaRiskToken.current = true;
      setCaptchaLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoFaErrorMessage, errorMessage, triggerOneTimeCallRetry]);

  // organisation flow.
  useEffect(() => {
    if (!currentRef.current) {
      if (domainType === "organisation") {
        // if url like org.cp.xxx => should not re-render => keep org login form.
        dispatch(LoginAction.setShowVerifyOrganisation(true));
        handleLoginOrganisationFlow();
        currentRef.current = true;
      } else {
        setIsOrganisationLoginOriginal(false);
        currentRef.current = false;
      }
    }

    return () => {
      // reset ref states.
      currentRef.current = false;
      triggerOneTimeCallRetry.current = false;
      shouldShowRecaptchaRiskToken.current = false;
      dispatch(LoginAction.clearLoginState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRef, domainType]);

  // web authentication flow.
  useEffect(() => {
    if (errorMessage?.code === "1010") {
      if (!errorMessage.totp && errorMessage?.challenge) {
        processWebAuthnAuthentication();
      }
    }
  }, [errorMessage, processWebAuthnAuthentication]);

  // recaptcha effect.
  useEffect(() => {
    if (captchaType?.provider === "turnstile") {
      /**
       * The Turnstile token is one-time use — it expires quickly (typically within 2 minutes).
       After sending it to your backend (or after any failed verification), you’ll usually need to refresh / reset the widget to generate a new token.
       */

      const interval = setInterval(() => {
        if (
          typeof window !== "undefined" &&
          window?.turnstile &&
          turnstileRef?.current
        ) {
          // eslint-disable-next-line no-console
          console.log("call interval !!!");
          clearInterval(interval);

          if (turnstileRef.current.hasChildNodes()) {
            // avoid call re-render multiple times
            console.warn(
              "Cloudflare Turnstile has already been rendered. Prevent !!!"
            );
            return;
          }

          window.turnstile?.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: "invisible",
            callback: (turnstileToken: string) => {
              if (tempTurnstileAction.current.action !== "") {
                prodModeLog(
                  "Callback token from turnstile, will trigger function " +
                    tempTurnstileAction.current.action
                );
                handleCallbackFunction(turnstileToken);
              } else {
                prodModeLog("No action with callback function !!!");
              }
            },
            "expired-callback": () => {
              setCaptchaLoading(false);
              window.turnstile?.reset(turnstileRef.current!);
            },
            "error-callback": (turnstileErrorCode) => {
              // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
              setCaptchaLoading(false);
              // eslint-disable-next-line no-console
              console.log({ turnstileErrorCode });
              // window.turnstile?.reset(turnstileRef.current!);

              setTurnstileError(
                `Cloudflare Turnstile get error with code: ${turnstileErrorCode}`
              );
            },
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaType]);

  if (isAuthenticated) navigate(pathname);

  return (
    <>
      <div ref={turnstileRef}></div>
      {loginFormType === LoginFormType.DEFAULT ? (
        <>
          <OriginalLoginForm
            t={t}
            shouldShowCurrentErrorMessage={shouldShowRecaptchaRiskToken.current}
            formFunctions={{
              onNextOrganisation,
              setOrganisationId,
              setStateForm,
              onRedirectToSamlIdp,
              onLogin,
            }}
            formLoading={{
              loading,
              smalLoading,
              captchaLoading: captchaLoading,
            }}
            formErrors={{
              errorMessage,
              // @ts-ignore
              samlError,
              // @ts-ignore
              organisationSettingsErrorMessage,
              twoFaErrorMessage,
              forgottenOrganisationErrorMessage,
              browserError,
              turnstileError,
            }}
            formState={{
              showVerifyOrganisation,
              organisationId,
              stateForm,
              organisationSettings,
              // @ts-ignore
              selectedSaml,
              isOrganisationLoginOriginal,
              hasForgottenOrganisation,
            }}
            // @ts-ignore
            socialErrorMessage={socialErrorMessage}
            // @ts-ignore
            setSocialErrorMessage={setSocialErrorMessage}
          />
        </>
      ) : (
        twoFactorContainerForm()
      )}
    </>
  );
};
